import React from 'react';
import { Box, IconButton, Tooltip, Divider, useTheme, useMediaQuery, Menu, MenuItem } from '@mui/material';
import {
  TableChart,
  ViewColumn,
  TableRows,
  LooksOne,
  LooksTwo,
  Looks3,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  InsertPhoto,
  FormatQuote,
  Code,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatStrikethrough,
  HorizontalRule as HorizontalRuleIcon,
  Undo,
  Redo,
  MoreHoriz,
} from '@mui/icons-material';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import CodeBlock from '@tiptap/extension-code-block';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';

const ColumnsExtension = Extension.create({
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          columns: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('data-columns'),
            renderHTML: (attributes: Record<string, any>) => {
              if (!attributes.columns) return {};
              return { 'data-columns': attributes.columns };
            },
          },
        },
      },
    ];
  },
});

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  theme?: 'light' | 'dark';
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, theme = 'light' }) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'code-block',
          },
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      CodeBlock,
      HorizontalRule,
      Image,
      ColumnsExtension,
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  // Define mobile menu items
  const mobileMenuItems = [
    { icon: <FormatListBulleted fontSize="small" />, title: "Bullet List", action: () => editor.chain().focus().toggleBulletList().run() },
    { icon: <FormatListNumbered fontSize="small" />, title: "Numbered List", action: () => editor.chain().focus().toggleOrderedList().run() },
    { icon: <Code fontSize="small" />, title: "Code Block", action: () => editor.chain().focus().toggleCodeBlock().run() },
    { icon: <FormatQuote fontSize="small" />, title: "Blockquote", action: () => editor.chain().focus().toggleBlockquote().run() },
    { icon: <TableChart fontSize="small" />, title: "Insert Table", action: () => editor.chain().focus().insertTable({ rows: 2, cols: 2 }).run() },
    { icon: <ViewColumn fontSize="small" />, title: "Add Column", action: () => editor.chain().focus().addColumnAfter().run(), disabled: !editor.can().addColumnAfter() },
    { icon: <TableRows fontSize="small" />, title: "Add Row", action: () => editor.chain().focus().addRowAfter().run(), disabled: !editor.can().addRowAfter() },
    { icon: <InsertPhoto fontSize="small" />, title: "Insert Image", action: () => {
      const url = window.prompt('Enter Image URL');
      if (url) editor.chain().focus().setImage({ src: url }).run();
    }},
    { icon: <HorizontalRuleIcon fontSize="small" />, title: "Horizontal Line", action: () => editor.chain().focus().setHorizontalRule().run() },
  ];

  return (
    <Box
      sx={{
        border: '1px solid #ddd',
        borderRadius: '4px',
        backgroundColor: theme === 'dark' ? '#1e1e1e' : '#fff',
        color: theme === 'dark' ? '#fff' : '#000',
        width: '100%', 
    overflow: 'hidden',
        '& .tiptap': {
          minHeight: isMobile ? '200px' : '300px',
          padding: isMobile ? '8px' : '16px',
          '& [data-columns]': {
            columnGap: '2rem',
            padding: '1rem 0',
          },
          '& [data-columns="2"]': { columnCount: 2 },
          '& [data-columns="3"]': { columnCount: 3 },
          '& table': {
            borderCollapse: 'collapse',
            margin: '16px 0',
            width: '100%',
            '& td, & th': {
              border: '1px solid #ddd',
              padding: '8px',
            },
            '& th': {
              backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f5f5f5',
            },
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
          },
          '& .code-block': {
            backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f5f5f5',
            padding: '0.75rem',
            borderRadius: '4px',
            fontFamily: 'monospace',
          },
        },
      }}
    >
      <Box sx={{ 
        p: 1, 
        display: 'flex', 
        gap: isMobile ? 0.5 : 1,
        flexWrap: 'wrap',
        whiteSpace: 'nowrap',
        overflowX: 'auto', 
    maxWidth: '100%', 
        '& .MuiDivider-vertical': {
          display: isMobile ? 'none' : 'inherit',
          mx: 0.5
        }
      }}>
        {/* Undo/Redo */}
        <Tooltip title="Undo">
          <IconButton onClick={() => editor.chain().focus().undo().run()} size="small">
            <Undo fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Redo">
          <IconButton onClick={() => editor.chain().focus().redo().run()} size="small">
            <Redo fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        {/* Headings */}
        <Tooltip title="Heading 1">
          <IconButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
            size="small"
          >
            <LooksOne fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Heading 2">
          <IconButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
            size="small"
          >
            <LooksTwo fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Heading 3">
          <IconButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
            size="small"
          >
            <Looks3 fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        {/* Text Formatting */}
        <Tooltip title="Bold">
          <IconButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'primary' : 'default'}
            size="small"
          >
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Italic">
          <IconButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'primary' : 'default'}
            size="small"
          >
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Strikethrough">
          <IconButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            color={editor.isActive('strike') ? 'primary' : 'default'}
            size="small"
          >
            <FormatStrikethrough fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        {/* Alignment */}
        <Tooltip title="Align Left">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
            size="small"
          >
            <FormatAlignLeft fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Center">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
            size="small"
          >
            <FormatAlignCenter fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Right">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
            size="small"
          >
            <FormatAlignRight fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Justify">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
            size="small"
          >
            <FormatAlignJustify fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Desktop-only items */}
        {!isMobile && (
          <>
            <Divider orientation="vertical" flexItem />

            <Tooltip title="Bullet List">
              <IconButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                color={editor.isActive('bulletList') ? 'primary' : 'default'}
                size="small"
              >
                <FormatListBulleted fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Numbered List">
              <IconButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                color={editor.isActive('orderedList') ? 'primary' : 'default'}
                size="small"
              >
                <FormatListNumbered fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Code Block">
              <IconButton
                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                size="small"
              >
                <Code fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Blockquote">
              <IconButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                color={editor.isActive('blockquote') ? 'primary' : 'default'}
                size="small"
              >
                <FormatQuote fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Mobile menu */}
        {isMobile && (
          <>
            <IconButton size="small" onClick={handleMenuOpen}>
              <MoreHoriz fontSize="small" />
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{ dense: true }}
            >
              {mobileMenuItems.map((item) => (
                <MenuItem
                  key={item.title}
                  onClick={() => {
                    item.action();
                    handleMenuClose();
                  }}
                  disabled={item.disabled}
                >
                  {item.icon}
                  <Box component="span" sx={{ ml: 1 }}>{item.title}</Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* Desktop-only table controls */}
        {!isMobile && (
          <>
            <Divider orientation="vertical" flexItem />

            <Tooltip title="Insert Table">
              <IconButton
                onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 2 }).run()}
                size="small"
              >
                <TableChart fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Add Column">
              <IconButton
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                disabled={!editor.can().addColumnAfter()}
                size="small"
              >
                <ViewColumn fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Add Row">
              <IconButton
                onClick={() => editor.chain().focus().addRowAfter().run()}
                disabled={!editor.can().addRowAfter()}
                size="small"
              >
                <TableRows fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Insert Image">
              <IconButton
                onClick={() => {
                  const url = window.prompt('Enter Image URL');
                  if (url) editor.chain().focus().setImage({ src: url }).run();
                }}
                size="small"
              >
                <InsertPhoto fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Horizontal Line">
              <IconButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                size="small"
              >
                <HorizontalRuleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <EditorContent editor={editor} />
    </Box>
  );
};

export default RichTextEditor;